/**
 * Project Team Panel Component
 * 
 * Manages project-scoped roles and members.
 * - Roles: Create, edit, delete project roles
 * - Members: Add one member at a time with one role, list members, remove member
 */
import React, { useState } from "react";
import {
  Button,
  Input,
  Select,
  Table,
  Modal,
  Form,
  Tag,
  Space,
  Popconfirm,
  Card,
  Avatar,
  Empty,
  Spin,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import useProjectRoles from "hooks/projects/useProjectRoles";
import useProjectMembers from "hooks/projects/useProjectMembers";
import { useUserManagement } from "hooks";
import { useNavigate } from "react-router-dom";
import "./ProjectTeamPanel.css";

const { Option } = Select;

/**
 * ProjectTeamPanel - Main container for team management
 * @param {string} projectId - The project ID
 */
const ProjectTeamPanel = ({ projectId, canManageTeam = false }) => {
  const [activeSubTab, setActiveSubTab] = useState("members");

  if (!projectId) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Empty description="No project selected" />
      </div>
    );
  }

  return (
    <div className="project-team-panel">
      {/* Sub-tab navigation */}
      <div className="team-sub-tabs" style={{ marginBottom: 16 }}>
        <Button
          type={activeSubTab === "members" ? "primary" : "default"}
          icon={<TeamOutlined />}
          onClick={() => setActiveSubTab("members")}
          style={{ marginRight: 8 }}
        >
          Members
        </Button>
        <Button
          type={activeSubTab === "roles" ? "primary" : "default"}
          icon={<UserOutlined />}
          onClick={() => setActiveSubTab("roles")}
        >
          Roles
        </Button>
      </div>

      {/* Content based on active sub-tab */}
      {activeSubTab === "members" ? (
        <MembersPanel projectId={projectId} canManageTeam={canManageTeam} />
      ) : (
        <RolesPanel projectId={projectId} canManageTeam={canManageTeam} />
      )}
    </div>
  );
};

/**
 * RolesPanel - Manage project roles
 */
const RolesPanel = ({ projectId, canManageTeam }) => {
  const { roles, loading: rolesLoading, createRole, updateRole, removeRole } =
    useProjectRoles(projectId);
  const { members, loading: membersLoading } = useProjectMembers(projectId);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form] = Form.useForm();

  // Check if a role is assigned to any member
  const isRoleAssigned = (roleId) => {
    return members.some((member) => member.roleId === roleId);
  };

  const handleCreate = () => {
    if (!canManageTeam) {
      message.warning("Only the project admin can manage team roles.");
      return;
    }
    setEditingRole(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = (role) => {
    if (!canManageTeam) {
      message.warning("Only the project admin can manage team roles.");
      return;
    }
    setEditingRole(role);
    form.setFieldsValue({
      name: role.name,
      code: role.code,
      description: role.description,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (roleId) => {
    if (!canManageTeam) {
      message.warning("Only the project admin can manage team roles.");
      return;
    }
    const success = await removeRole(roleId);
    if (success) {
      message.success("Role deleted successfully");
    }
  };

  const handleSubmit = async (values) => {
    if (!canManageTeam) {
      message.warning("Only the project admin can manage team roles.");
      return;
    }

    const payload = { ...values, active: true };
    if (editingRole) {
      await updateRole(editingRole.id, payload);
    } else {
      await createRole(payload);
    }
    setIsModalOpen(false);
    form.resetFields();
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code) => <Tag color="blue">{code}</Tag>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      render: () => <Tag color="green">Active</Tag>,
    },
    ...(canManageTeam
      ? [
          {
            title: "Actions",
            key: "actions",
            render: (_, record) => {
              const isAssigned = isRoleAssigned(record.id);
              return (
                <Space>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                  {!isAssigned && (
                    <Popconfirm
                      title="Delete this role?"
                      description="This action cannot be undone."
                      onConfirm={() => handleDelete(record.id)}
                      okText="Delete"
                      cancelText="Cancel"
                    >
                      <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )}
                </Space>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <Card
      title="Project Roles"
      extra={canManageTeam ? (
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Add Role
        </Button>
      ) : null}
    >
      {!canManageTeam && (
        <div style={{ marginBottom: 12, color: "#8c8c8c" }}>
          Only the project admin can create, edit, or delete roles.
        </div>
      )}
      <Spin spinning={rolesLoading || membersLoading}>
        {roles.length > 0 ? (
          <Table
            dataSource={roles}
            columns={columns}
            rowKey="id"
            pagination={false}
          />
        ) : (
          <Empty description="No roles defined yet" />
        )}
      </Spin>

      <Modal
        title={editingRole ? "Edit Role" : "Create Role"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        zIndex={4000}
        centered
        mask={{closable: false}}
        rootClassName="project-team-modal-root"
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Role Name"
            rules={[{ required: true, message: "Please enter role name" }]}
          >
            <Input placeholder="e.g., Developer" />
          </Form.Item>

          <Form.Item
            name="code"
            label="Role Code"
            rules={[{ required: true, message: "Please enter role code" }]}
          >
            <Input placeholder="e.g., DEVELOPER" disabled={!!editingRole} />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea placeholder="Role description..." rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRole ? "Update" : "Create"}
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

/**
 * MembersPanel - Manage project members
 */
const MembersPanel = ({ projectId, canManageTeam }) => {
  const navigate = useNavigate();
  const {
    members,
    loading: membersLoading,
    addMember,
    updateMemberRole,
    removeMember,
  } = useProjectMembers(projectId);

  const { roles, loading: rolesLoading } = useProjectRoles(projectId);
  const { searchUserProfiles } = useUserManagement();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isChangeRoleModalOpen, setIsChangeRoleModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [addForm] = Form.useForm();
  const [roleForm] = Form.useForm();

  const [searchedUsers, setSearchedUsers] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  const handleSearchUser = async (value) => {
    const query = String(value || "").trim();
    if (query.length < 3) {
      setSearchedUsers([]);
      return;
    }

    setSearchingUsers(true);
    try {
      const users = (await searchUserProfiles(query)) || [];
      const activeMemberUserIds = new Set(members.map((m) => m.userId));
      setSearchedUsers(users.filter((u) => !activeMemberUserIds.has(u.id)));
    } catch (error) {
      console.error("User search error:", error);
      setSearchedUsers([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleAddMember = async (values) => {
    if (!canManageTeam) {
      message.warning("Only the project admin can manage team members.");
      return;
    }

    const result = await addMember({
      userId: values.userId,
      roleId: values.roleId,
    });

    if (result) {
      setIsAddModalOpen(false);
      addForm.resetFields();
      message.success("Member added successfully");
    }
  };

  const handleChangeRole = async (values) => {
    if (!canManageTeam) {
      message.warning("Only the project admin can manage team members.");
      return;
    }
    if (!selectedMember) return;

    const result = await updateMemberRole(selectedMember.id, {
      roleId: values.roleId,
    });

    if (result) {
      setIsChangeRoleModalOpen(false);
      setSelectedMember(null);
      roleForm.resetFields();
      message.success("Role updated successfully");
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!canManageTeam) {
      message.warning("Only the project admin can manage team members.");
      return;
    }
    const success = await removeMember(memberId);
    if (success) {
      message.success("Member removed successfully");
    }
  };

  const openChangeRoleModal = (member) => {
    if (!canManageTeam) {
      message.warning("Only the project admin can manage team members.");
      return;
    }
    setSelectedMember(member);
    roleForm.setFieldsValue({ roleId: member.role?.id });
    setIsChangeRoleModalOpen(true);
  };

  const handleViewUserDetails = (userId) => {
    navigate(`/olitask/users/${userId}`);
  };

  const columns = [
    {
      title: "Member",
      key: "member",
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} src={record.user?.avatar} />
          <a
            onClick={() => handleViewUserDetails(record.userId)}
            style={{ cursor: "pointer" }}
          >
            {record.user?.name || record.user?.email || record.userId || "Unknown User"}
            {record.user?.email && (
              <span style={{ color: "#8c8c8c", marginLeft: 4 }}>
                ({record.user.email})
              </span>
            )}
          </a>
        </Space>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role, record) => {
        // First check if role object exists with name property
        if (role?.name) {
          return <Tag color="blue">{role.name}</Tag>;
        }
        // Check if roleName exists directly (from backend)
        if (record?.roleName) {
          return <Tag color="blue">{record.roleName}</Tag>;
        }
        // Check if roleId exists but role name is missing
        if (record?.roleId) {
          return <Tag color="orange">Role Assigned</Tag>;
        }
        return <Tag color="default">No Role</Tag>;
      },
    },
    {
      title: "Joined",
      dataIndex: "joinedAt",
      key: "joinedAt",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "-"),
    },
    ...(canManageTeam
      ? [
          {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
              <Space>
                <Button
                  type="link"
                  onClick={() => openChangeRoleModal(record)}
                >
                  Change Role
                </Button>
                <Popconfirm
                  title="Remove this member?"
                  description="The member will be removed from this project."
                  onConfirm={() => handleRemoveMember(record.id)}
                  okText="Remove"
                  cancelText="Cancel"
                >
                  <Button type="text" danger>
                    Remove
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  const isLoading = membersLoading || rolesLoading;

  return (
    <Card
      title="Team Members"
      extra={canManageTeam ? (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsAddModalOpen(true)}
        >
          Add Member
        </Button>
      ) : null}
    >
      {!canManageTeam && (
        <div style={{ marginBottom: 12, color: "#8c8c8c" }}>
          Only the project admin can add members, remove members, assign roles, or transfer admin rights.
        </div>
      )}
      <Spin spinning={isLoading}>
        {members.length > 0 ? (
          <Table
            dataSource={members}
            columns={columns}
            rowKey="id"
            pagination={false}
          />
        ) : (
          <Empty description="No team members yet" />
        )}
      </Spin>

      {/* Add Member Modal */}
      <Modal
        title="Add Team Member"
        open={isAddModalOpen}
        onCancel={() => {
          setIsAddModalOpen(false);
          addForm.resetFields();
          setSearchedUsers([]);
        }}
        zIndex={4000}
        centered
        maskClosable={false}
        rootClassName="project-team-modal-root"
        footer={null}
      >
        <Form form={addForm} layout="vertical" onFinish={handleAddMember}>
          <Form.Item
            name="userId"
            label="Select User"
            rules={[{ required: true, message: "Please select a user" }]}
          >
            <Select
              showSearch
              placeholder="Search for a user..."
              onSearch={handleSearchUser}
              loading={searchingUsers}
              filterOption={false}
              notFoundContent={
                searchingUsers
                  ? "Searching users..."
                  : "Type at least 3 characters to search users"
              }
            >
              {searchedUsers.map((user) => (
                <Option key={user.id} value={user.id}>
                  <Space>
                    <Avatar size="small" src={user.profilePhoto} icon={<UserOutlined />} />
                    {user.fullName || user.email}
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="roleId"
            label="Assign Role"
            rules={[{ required: true, message: "Please select a role" }]}
          >
            <Select placeholder="Select a role">
              {roles.map((role) => (
                <Option key={role.id} value={role.id}>
                  {role.name}
                  {/* {role.active === false && "(Inactive)"} */}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {roles.length === 0 && (
            <div style={{ color: "#ff4d4f", marginBottom: 16 }}>
              No roles defined yet. Please create roles first.
            </div>
          )}

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                disabled={roles.length === 0}
              >
                Add Member
              </Button>
              <Button onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Change Role Modal */}
      <Modal
        title="Change Member Role"
        open={isChangeRoleModalOpen}
        onCancel={() => {
          setIsChangeRoleModalOpen(false);
          setSelectedMember(null);
          roleForm.resetFields();
        }}
        zIndex={4000}
        centered
        maskClosable={false}
        rootClassName="project-team-modal-root"
        footer={null}
      >
        <Form form={roleForm} layout="vertical" onFinish={handleChangeRole}>
          <Form.Item
            name="roleId"
            label="New Role"
            rules={[{ required: true, message: "Please select a role" }]}
          >
            <Select placeholder="Select a role">
              {roles.map((role) => (
                <Option key={role.id} value={role.id}>
                  {role.name} {role.active === false && "(Inactive)"}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Update Role
              </Button>
              <Button onClick={() => setIsChangeRoleModalOpen(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ProjectTeamPanel;
